import { useEffect, useState } from 'react';
import { ArenaProvider, useArena } from './state/ArenaContext.tsx';
import { createBackend } from './state/createBackend.ts';
import type { RoomBackend } from './state/types.ts';
import { Toasts } from './app/TopBar.tsx';
import { AmbientBackdrop } from './components/system/AmbientBackdrop.tsx';
import { MatrixBackdrop } from './components/system/MatrixBackdrop.tsx';
import { PointerLighting, ScrollProgress } from './components/system/PointerLighting.tsx';
import { RemoteAudioManager } from './components/system/RemoteAudioManager.tsx';
import HomePage from './pages/HomePage.tsx';
import DebaterHudPage from './pages/DebaterHudPage.tsx';
import CoachConsolePage from './pages/CoachConsolePage.tsx';
import ReviewPage from './pages/ReviewPage.tsx';
import SettingsPage from './pages/SettingsPage.tsx';

function AppRoutes() {
  const { state } = useArena();
  return (
    <div className="relative min-h-screen">
      <MatrixBackdrop />
      <AmbientBackdrop />
      <PointerLighting />
      <ScrollProgress />
      <div className="relative z-10 min-h-screen">
        {state.route === 'home' && <HomePage />}
        {state.route === 'arena' && <DebaterHudPage />}
        {state.route === 'coach' && <CoachConsolePage />}
        {state.route === 'review' && <ReviewPage />}
        {state.route === 'settings' && <SettingsPage />}
        <RemoteAudioManager />
        <Toasts />
      </div>
    </div>
  );
}

export default function App() {
  const [backend, setBackend] = useState<RoomBackend | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let instance: RoomBackend | null = null;
    createBackend()
      .then((created) => {
        if (cancelled) {
          created.dispose();
          return;
        }
        instance = created;
        setBackend(created);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
      instance?.dispose();
    };
  }, []);

  if (error) {
    return (
      <main className="grid min-h-screen place-items-center p-8">
        <div className="max-w-md rounded-xl border border-assert-violation/40 bg-assert-violation/10 p-6">
          <h1 className="text-lg font-semibold text-slate-100">应用初始化失败</h1>
          <p className="mt-2 text-sm text-slate-300">{error}</p>
          <p className="mt-3 text-xs text-slate-500">
            提示：可用 <code className="mono">?demo=1</code> 打开离线演示模式。
          </p>
        </div>
      </main>
    );
  }

  if (!backend) {
    return (
      <main className="grid min-h-screen place-items-center">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-team-aff" />
          <p className="mt-4 text-sm text-slate-400">正在加载纵横演武场…</p>
        </div>
      </main>
    );
  }

  return (
    <ArenaProvider backend={backend}>
      <AppRoutes />
    </ArenaProvider>
  );
}
